import React, { useEffect, useRef, useState, useCallback } from 'react'
import { unstable_batchedUpdates as batch } from 'react-dom'
import ReactDOM from 'react-dom'
import debounce from 'lodash.debounce'
import classnames from 'classnames'

import Tooltip from '../Tooltip/Tooltip'
import Dropdown from '../DropDown/Dropdown'
import Icon from '../Icon2/Icon'
import Button from '../Button/Button'
import './BulkActionPanel.scss'

export type BulkActionItem = {
  label: string | React.ReactNode
  value?: string
  cb: (actionCbArg: { data: any, type: string }) => void
  type?: string
  icon?: string
  showSelected?: boolean
  testId?: string
  ariaLabel?: string
  [key: string]: any
}

export type BulkActionPanelProps = {
  bulkActionList: BulkActionItem[]
  selectedData: any
  updatedParentWidth: number
  testId?: string
  trimActionsFromLeft?: boolean
  activeValue?: string
  onActiveValueChange?: (value: string) => void
  onClose?: () => void
  arrangeFromStart?: boolean
}
const throttle = (f) => {
  let token = null,
    lastArgs = null
  const invoke = () => {
    f(...lastArgs)
    token = null
  }
  const result = (...args) => {
    lastArgs = args
    if (!token) {
      token = requestAnimationFrame(invoke)
    }
  }
  result.cancel = () => token && cancelAnimationFrame(token)
  return result
}

const useRefEffect = (handler) => {
  const storedValue = useRef<HTMLElement>()
  const unsubscribe = useRef<HTMLElement>()
  const result = useCallback(
    (value) => {
      storedValue.current = value
      if (unsubscribe.current) {
        //unsubscribe.current()
        unsubscribe.current = undefined
      }
      if (value) {
        unsubscribe.current = handler(value)
      }
    },
    [handler]
  )
  useEffect(() => {
    result(storedValue.current)
  }, [result])
  return result
}

// combine several `ref`s into one
// list of refs is supposed to be immutable after first render
const useCombinedRef = (...refs) => {
  const initialRefs = useRef(refs)
  return useCallback((value) => {
    initialRefs.current.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(value)
      } else {
        ref.current = value
      }
    })
  }, [])
}

// create a ref to subscribe to given element's event
const useDomEvent = (name, handler) => {
  return useCallback(
    (elem) => {
      elem.addEventListener(name, handler)
      return () => {
        elem.removeEventListener(name, handler)
      }
    },
    [name, handler]
  )
}

// callback with persistent reference,
// but updated on every render
const usePersistentCallback = (f) => {
  const realF = useRef(f)
  useEffect(() => {
    realF.current = f
  }, [f])
  return useCallback((...args) => {
    return realF.current(...args)
  }, [])
}

// persistent reference to identity function
const useDraggable = (updatedParentWidth?: number) => {
  const [pressed, setPressed] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 140 })
  const size = useRef({ x: Infinity, y: Infinity })
  const handleDrag = useCallback(
    ({ x, y }) => ({
      x: Math.max(0, Math.min(x, size.current.x)),
      y: Math.max(0, Math.min(y, size.current.y))
    }),
    []
  )
  const ref = useRef<HTMLElement>()
  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) {
      return
    }
    setPressed(true)
  }, [])

  useEffect(() => {
    if (!ref.current) return
    const elem = ref.current
    if (elem) {
      elem.style.userSelect = 'none'
      setPosition({ x: elem.getBoundingClientRect().left, y: elem.getBoundingClientRect().top })
      return () => {
        elem.style.userSelect = 'auto'
      }
    }
  }, [updatedParentWidth])
  const subscribeMouseDown = useDomEvent('pointerdown', handleMouseDown)
  const ref2 = useRefEffect(subscribeMouseDown)
  const combinedRef = useCombinedRef(ref, ref2)
  const persistentOnDrag = usePersistentCallback(handleDrag)
  useEffect(() => {
    if (!pressed) {
      return
    }
    let delta = position,
      lastPosition = position
    const applyTransform = () => {
      if (!ref.current) {
        return
      }

      const { x, y } = lastPosition
      ref.current.style.transform = `translate(${x}px, ${y}px)`
      ref.current.style.left = "0px"
    }
    const handleMouseMove = throttle(({ movementX, movementY }) => {
      let { x, y } = delta
      if (x >= window.innerWidth - 60) {
        x = window.innerWidth - 60
      }
      if (y >= window.innerHeight - 30) {
        y = window.innerHeight - 30
      }
      delta = { x: x + movementX, y: y + movementY }
      lastPosition = persistentOnDrag(delta)
      applyTransform()
    })
    const handleMouseUp = (e) => {
      handleMouseMove(e)
      batch(() => {
        setPressed(false)
        setPosition(lastPosition)
      })
    }
    const terminate = () => {
      lastPosition = position
      applyTransform()
      setPressed(false)
    }
    const handleKeyDown = (e) => {
      if (e.code === 'Escape') {
        e.preventDefault()
        terminate()
      }
    }
    document.addEventListener('pointermove', handleMouseMove)
    document.addEventListener('pointerup', handleMouseUp)
    document.addEventListener('keydown', handleKeyDown)
    window.addEventListener('blur', terminate)
    return () => {
      handleMouseMove.cancel()
      document.removeEventListener('pointermove', handleMouseMove)
      document.removeEventListener('pointerup', handleMouseUp)
      document.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('blur', terminate)
    }
  }, [position, pressed, persistentOnDrag])
  return { combinedRef, pressed, position }
}

function getTextWidth(text, font) {
  // Create an off-screen canvas
  const canvas: any = document.createElement('canvas');
  const context: any = canvas.getContext('2d') || { font: '16px Inter', measureText: () => { return { width: 0 } } };

  // Set the font for measurement
  context.font = font || '16px Inter'; // Default font is 16px Arial

  // Measure the width of the text
  const metrics = context.measureText(text);

  // Return the width of the text
  return metrics.width;
}

// this function is to show no. of items in bulk action panel and rest in show more dropdwn
const isDropdownVisible = (allMenusWidth, bulkActionList, panelWidth) => {
  let sliceCount = 0

  if (allMenusWidth > (panelWidth - 300)) {
    const gapWidth = 180
    let totalWidth = gapWidth
    bulkActionList.forEach((item) => {
      const itemWidth = getTextWidth(item.label, 'bold 16px Inter') + 40;
      totalWidth += itemWidth
      if (totalWidth > (panelWidth - 150)) {
        sliceCount++
      }
    })
  }
  return sliceCount
}

// This component is for V2 Bulk Action Panel
export const BulkActionPanel: React.FC<BulkActionPanelProps> = (props) => {

  // const { bulkActionList, selectedData, updatedParentWidth, testId = 'cs-bulk-action-panel' , trimActionsFromLeft = false} = props
  const { bulkActionList, selectedData, updatedParentWidth, testId = 'cs-bulk-action-panel', activeValue, onActiveValueChange, onClose, arrangeFromStart, trimActionsFromLeft = false } = props
  const [sliceCount, setSliceCount] = useState(0)

  useEffect(() => {

    function getCompleteWidth(bulkActionList) {
      let completeWidth = 0
      bulkActionList.forEach((item) => {
        completeWidth += getTextWidth(item.label, 'bold 16px Inter') + 40;
      })
      return completeWidth
    }

    const calculateAllMenuItemsWidth = getCompleteWidth(bulkActionList)

    const handleResize = () => {
      // @ts-ignore
      const updatedParentWidth = document.querySelector('[data-test-id="cs-bulk-action-panel"]')?.offsetWidth || props?.updatedParentWidth
      const sliceCount = isDropdownVisible(calculateAllMenuItemsWidth, bulkActionList, updatedParentWidth)
      if (sliceCount > 0) {
        setSliceCount(sliceCount === bulkActionList.length ? sliceCount - 1 : sliceCount)
      } else {
        setSliceCount(0)
      }
    }

    handleResize()
  }, [updatedParentWidth])

  const hanldeClick = (action) => {
    const type = typeof (action.value || action.label) === 'string' ? (action.value || action.label).toLowerCase() : ''
    action.cb({ data: selectedData, type })
  }
  const { combinedRef } = useDraggable(updatedParentWidth)
  let foundDiv = document.getElementById('tableBulkActionNode')
  let div: any

  if (foundDiv) {
    div = foundDiv
  } else {
    div = document.createElement('div')
    div.setAttribute('id', 'tableBulkActionNode')
    document.body.appendChild(div)
  }

  function closeTableBulkAction() {
    if (div.parentNode) {
      div.parentNode.removeChild(div)
    }
  }

  return ReactDOM.createPortal(
    <div ref={combinedRef} className={'bulk-action-wrapper'} style={{ width: updatedParentWidth * 0.82 + 'px' }} data-test-id={testId}>
      <BulkActionPanelItem
        bulkActionList={bulkActionList}
        hanldeClick={hanldeClick}
        showDropdown={sliceCount > 0}
        sliceCount={bulkActionList.length - sliceCount}
        originalSliceCount={sliceCount}
        trimActionsFromLeft={trimActionsFromLeft}
        activeValue={activeValue}
        onActiveValueChange={onActiveValueChange}
        onClose={onClose}
        arrangeFromStart={arrangeFromStart}
      />
    </div>,
    div
  )
}

{/* export const BulkActionPanelItem = ({ bulkActionList=[], hanldeClick, showDropdown, sliceCount,  }) => { */}
  


export const BulkActionPanelItem = ({ bulkActionList, hanldeClick, showDropdown, sliceCount, activeValue, onActiveValueChange, onClose, arrangeFromStart, originalSliceCount, trimActionsFromLeft=false }) => {
  const [selectedActions, setSelectedActions] = useState(new Set())

  let bulkActionListItems = []
  let trimmedBulkActions = []

  if(trimActionsFromLeft) {
    bulkActionListItems = originalSliceCount > 0 && showDropdown ? [bulkActionList[0], ...bulkActionList.slice(1 + originalSliceCount)] : bulkActionList
    // Everything trimmed off goes here
    trimmedBulkActions = bulkActionList.slice(1, originalSliceCount + 1)
  } else {
    bulkActionListItems = showDropdown ? bulkActionList.slice(0, sliceCount) : bulkActionList
    trimmedBulkActions = bulkActionList.slice(sliceCount, bulkActionList.length)
  }

  // Initialize with activeValue if provided
  useEffect(() => {
    if (activeValue !== undefined && activeValue !== null) {
      setSelectedActions(new Set([activeValue]))
    }
  }, [activeValue])

  const handleActionClick = (action) => {
    if (activeValue !== undefined && activeValue !== null) {
      // Single selection - always set new selection, never deselect
      const actionValue = action.value || action.label
      setSelectedActions(new Set([actionValue]))

      // Update parent state if callback provided
      if (onActiveValueChange) {
        onActiveValueChange(actionValue)
      }
    }
    hanldeClick(action)
  }

  // let bulkActionListItems = showDropdown ? bulkActionList.slice(0, sliceCount) : bulkActionList
  return (
    <div
      className={classnames(
        'TableActionPanel',
        'flex-v-center',
        'Table-bulk-action',
        { 'flex-justify': !arrangeFromStart }
      )}
      tabIndex={0}
    >
      <div className="selected-items" >
        <div className="drag-icon">
          <Tooltip variantType="basic" content="Drag" position="top" showArrow={true}>
            <Icon icon="ActionBar" version='v2' size='small' />
          </Tooltip>
        </div>
      </div>
      <div
        className={classnames(!arrangeFromStart ? 'bulk-action-items' : 'bulk-action-items-arrange-from-start')}
      >
        {bulkActionListItems.map((action: any, i: number) => {
          return (
            <Button
              version="v2"
              id={`bulk-panel-action-item-${i}`}
              testId={action.testId}
              aria-label={action.ariaLabel}
              key={i}
              size="large"
              buttonType={action.type != 'primary' ? action.type : 'light'}
              icon={action.icon ? action.icon : undefined}
              onClick={() => handleActionClick(action)}
              className={classnames(
                action.showSelected && 'show-select-btn',
                activeValue !== undefined && activeValue !== null && selectedActions.has(action.value || action.label) && 'selected-action-btn'
              )}
            // onKeyDown={(e) => {
            //   //reiterating focus through list
            //   if (i === bulkActionListItems.length - 1 && e.key === 'Tab' && !showDropdown) {
            //     setTimeout(() => {
            //       document.getElementById('bulk-panel-action-item-0')?.focus()
            //     })
            //   }
            // }}
            >
              {action.label}
            </Button>
          )
        })}
        {showDropdown > 0 && bulkActionList?.length > bulkActionListItems?.length && (
          <Dropdown
            version="v1"
            list={trimmedBulkActions.map((list, dropdownIndex) => {
              const iconVersion = list?.icon?.includes('v2-') ? 'v2' : 'v1'
              const iconName = iconVersion === 'v2' ? list?.icon?.split('v2-')[1] : list.icon
              const iconClassName = list?.icon === 'Unpublish' ? 'Icon--unpublish' : ''
              return {
                label: (
                  <>
                    <Icon icon={iconName} version={iconVersion} className={iconClassName} />
                    <div data-test-id={list.testId}>{list.label}</div>
                  </>
                ),
                action: () => handleActionClick(list),
                default: true,
              }
            })}
            type="click">
            <div className="see-more-icon" data-test-id={'table-see-more-icon'}>
              <Icon icon="SeeMore" fill="white" size="small" height={24} width={24} />
            </div>
          </Dropdown>
        )}
      </div>
      {
        onClose && (
          <div className="close-button">
            <Button
              buttonType="light"
              onClick={() => onClose()}
              icon={'v2-CloseNoborder'}
              onlyIcon={true}
              size={'medium'}
              version={'v2'}
              iconProps={{ fill: '#F9F8FF' }}
            />
          </div>
        )
      }
    </div >
  )
}
